import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, ArrowRight, Sparkles } from 'lucide-react';
import { FaFacebook, FaInstagram, FaLinkedin } from 'react-icons/fa';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useRegion } from '../../../regions/RegionContext';
import { trackFormSubmit } from '../../../lib/gtag';
import { sendTransactionalEmail, escapeHtml } from '../../../services/email';

const Footer = () => {
  const { config } = useRegion();
  const currentYear = new Date().getFullYear();

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      toast.error('Please enter your email address.');
      return;
    }
    if (!emailPattern.test(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setSubscribing(true);
    try {
      await sendTransactionalEmail({
        to: [{ email: 'info@terces.io', name: 'Terces Solutions' }],
        replyTo: { email },
        subject: 'New Newsletter Subscription',
        htmlContent: `<p>A new visitor subscribed to the newsletter:</p><p><strong>${escapeHtml(
          email
        )}</strong></p>`,
      });
      trackFormSubmit('newsletter');
      toast.success("You're subscribed! Watch your inbox for updates.");
      setNewsletterEmail('');
    } catch (_) {
      toast.error('Something went wrong. Please try again later.');
    } finally {
      setSubscribing(false);
    }
  };

  const services = [
    { name: 'Process & Productivity Improvement', path: '/services/process-optimization' },
    { name: 'Menu Engineering', path: '/services/menu-engineering' },
    { name: 'Kitchen Performance Optimization', path: '/services/kitchen-operations' },
    { name: 'Inventory & Cost Control', path: '/services/inventory-management' },
    { name: 'Branding & Marketing', path: '/services/branding-marketing' },
    { name: '& More..', path: '/services' },
  ];

  const quickLinks = [
    { name: 'About Us', path: '/Ourstory' },
    { name: 'Our Services', path: '/Services' },
    { name: 'Case Studies', path: '/CaseStudies' },
    { name: 'Careers', path: '/Careers' },
    { name: 'Blog', path: '/Blogs' },
    { name: 'Contact', path: '/Contact' },
  ];

  const socialLinks = [
    { icon: FaFacebook, href: 'https://www.facebook.com/Terces1555', label: 'Facebook' },
    { icon: FaInstagram, href: 'https://www.instagram.com/terces_solutions/', label: 'Instagram' },
    { icon: FaLinkedin, href: 'https://www.linkedin.com/company/105919678/', label: 'LinkedIn' },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <footer className="relative bg-[#F6F4EF] overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      {/* Decorative Top Border */}
      <div className="relative h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />

      {/* Decorative Background Blobs */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2 }}
        viewport={{ once: true }}
        className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary rounded-full blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-yellow-400 rounded-full blur-3xl"
      />

      {/* Main Footer */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 md:py-20">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-16"
        >
          {/* Company Info - Takes 4 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-4">
            <Link to="/" className="inline-block mb-6">
              <motion.div 
                className="flex items-center gap-3"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-extrabold text-white">T</span>
                </div>
                <div>
                  <h3 className="text-2xl font-extrabold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                    Terces
                  </h3>
                  <p className="text-xs text-gray-600 uppercase tracking-wider font-semibold">Solutions</p>
                </div>
              </motion.div>
            </Link>
            
            <p className="text-gray-600 mb-8 leading-relaxed text-base">
              {config.content.footerBlurb}
            </p>
            
            {/* Newsletter Signup */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-primary" />
                <p className="text-sm font-semibold text-gray-900">Stay Updated</p>
              </div>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2" noValidate>
                <input
                  type="email"
                  placeholder="Your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  aria-label="Email address for newsletter"
                  className="flex-1 px-4 py-3 rounded-xl bg-white border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm text-gray-900 placeholder:text-gray-400"
                />
                <motion.button
                  type="submit"
                  disabled={subscribing}
                  aria-label="Subscribe to newsletter"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-5 py-3 bg-primary hover:bg-primary-dark text-white rounded-xl transition-colors shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </form>
            </div>

            {/* Social Links */}
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-4">Follow Us</p>
              <div className="flex gap-3">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-11 h-11 bg-white rounded-xl flex items-center justify-center hover:bg-primary transition-all group shadow-md hover:shadow-lg border-2 border-gray-200 hover:border-primary"
                  >
                    <social.icon className="w-5 h-5 text-primary group-hover:text-white transition-colors" />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Services - Takes 3 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-3">
            <h4 className="text-lg font-bold text-gray-900 mb-6 relative inline-block">
              Our Services
              <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-primary rounded-full"></div>
            </h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to={service.path}
                    className="group flex items-center gap-3 text-gray-600 hover:text-primary transition-colors text-sm"
                  >
                    <motion.span 
                      className="w-1.5 h-1.5 bg-primary/40 rounded-full group-hover:bg-primary transition-colors"
                      whileHover={{ scale: 1.5 }}
                    />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {service.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Quick Links - Takes 2 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <h4 className="text-lg font-bold text-gray-900 mb-6 relative inline-block">
              Quick Links
              <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-primary rounded-full"></div>
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="group flex items-center gap-3 text-gray-600 hover:text-primary transition-colors text-sm"
                  >
                    <motion.span 
                      className="w-1.5 h-1.5 bg-primary/40 rounded-full group-hover:bg-primary transition-colors"
                      whileHover={{ scale: 1.5 }}
                    />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {link.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info - Takes 3 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-3">
            <h4 className="text-lg font-bold text-gray-900 mb-6 relative inline-block">
              Get In Touch
              <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-primary rounded-full"></div>
            </h4>
            <ul className="space-y-5">
              <li>
                <div className="group flex items-start gap-4">
                  <motion.div 
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-11 h-11 bg-white rounded-xl flex items-center justify-center flex-shrink-0 border-2 border-gray-200 group-hover:border-primary group-hover:bg-primary shadow-md transition-all"
                  >
                    <MapPin className="w-5 h-5 text-primary group-hover:text-white transition-colors" strokeWidth={2} />
                  </motion.div>
                  <div className="pt-1">
                    <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Location</p>
                    <span className="text-gray-700 text-sm leading-relaxed">
                      {config.address.lines.map((line, index) => (
                        <span key={index}>
                          {index > 0 && <br />}
                          {line}
                        </span>
                      ))}
                    </span>
                    {config.address.note && (
                      <p className="text-xs text-gray-500 mt-1">{config.address.note}</p>
                    )}
                  </div>
                </div>
              </li>
              
              <li>
                {config.contact.showPhone ? (
                  <a href={config.contact.phoneTel} className="group flex items-start gap-4">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-11 h-11 bg-white rounded-xl flex items-center justify-center flex-shrink-0 border-2 border-gray-200 group-hover:border-primary group-hover:bg-primary shadow-md transition-all"
                    >
                      <Phone className="w-5 h-5 text-primary group-hover:text-white transition-colors" strokeWidth={2} />
                    </motion.div>
                    <div className="pt-1">
                      <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Phone</p>
                      <span className="text-gray-700 hover:text-primary transition-colors text-sm font-medium">
                        {config.contact.phoneDisplay}
                      </span>
                    </div>
                  </a>
                ) : (
                  <a
                    href={config.contact.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start gap-4"
                  >
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-11 h-11 bg-white rounded-xl flex items-center justify-center flex-shrink-0 border-2 border-gray-200 group-hover:border-primary group-hover:bg-primary shadow-md transition-all"
                    >
                      <Phone className="w-5 h-5 text-primary group-hover:text-white transition-colors" strokeWidth={2} />
                    </motion.div>
                    <div className="pt-1">
                      <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">WhatsApp</p>
                      <span className="text-gray-700 hover:text-primary transition-colors text-sm font-medium">
                        WhatsApp us
                      </span>
                    </div>
                  </a>
                )}
              </li>
              
              <li>
                <a href="mailto:info@terces.io" className="group flex items-start gap-4">
                  <motion.div 
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-11 h-11 bg-white rounded-xl flex items-center justify-center flex-shrink-0 border-2 border-gray-200 group-hover:border-primary group-hover:bg-primary shadow-md transition-all"
                  >
                    <Mail className="w-5 h-5 text-primary group-hover:text-white transition-colors" strokeWidth={2} />
                  </motion.div>
                  <div className="pt-1">
                    <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Email</p>
                    <span className="text-gray-700 hover:text-primary transition-colors text-sm font-medium">
                      info@terces.io
                    </span>
                  </div>
                </a>
              </li>
            </ul>

            {/* CTA Button */}
            <motion.div className="mt-8">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="group w-full px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <span>Schedule Consultation</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.5 }}
          className="w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent mt-16 mb-8"
        />

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col md:flex-row justify-between items-center gap-6 text-sm"
        >
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-3">
            <p className="text-gray-600 text-center md:text-left">
              © {currentYear} <span className="font-semibold text-gray-900">Terces Solutions</span>. All rights reserved.
            </p>
            <span className="hidden md:block w-1 h-1 bg-gray-400 rounded-full" />
            <p className="text-gray-500 text-xs">
              {config.content.footerTagline}
            </p>
          </div>
          
          <div className="flex items-center gap-6">
            <Link 
              to="/privacy-policy" 
              className="text-gray-600 hover:text-primary transition-colors font-medium text-sm"
            >
              Privacy Policy
            </Link>
            <span className="w-1 h-1 bg-gray-400 rounded-full" />
            <Link
              to="/terms"
              className="text-gray-600 hover:text-primary transition-colors font-medium text-sm"
            >
              Terms of Service
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Bottom Accent Line */}
      <div className="h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
    </footer>
  );
};

export default Footer;